<template>
  <div class="worker-setup">
    <h4 v-if="showHeading" class="worker-setup__heading">Local Worker Setup</h4>
    <ol class="worker-setup__steps">
      <li>
        <strong>Download the worker binary.</strong>
        <div class="worker-setup__downloads">
          <button
            v-for="platform in platforms"
            :key="platform.id"
            type="button"
            class="button button--ghost worker-setup__download"
            :disabled="downloading === platform.id"
            @click="downloadBinary(platform.id)"
          >
            {{ downloading === platform.id ? 'Downloading...' : platform.label }}
          </button>
        </div>
        <p class="worker-setup__note">Or build from source with `cd worker && make build`.</p>
      </li>
      <li>
        <strong>Run the worker.</strong>
        <pre class="worker-setup__code">API_KEY=YOUR_API_KEY beads-worker --api-url {{ orchestratorBaseUrl }}</pre>
      </li>
    </ol>
    <p class="worker-setup__note">
      The worker polls for queued runs and executes them through your local `claude` CLI. Make sure `claude` is available in `PATH`.
    </p>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useOrchestratorBaseUrl } from '@/composables/useOrchestratorBaseUrl.js'
import { apiErrorMessage } from '@/services/apiErrorMessage.js'

defineProps({
  showHeading: { type: Boolean, default: true },
})

const { orchestratorBaseUrl, apiHeaders } = useOrchestratorBaseUrl()
const downloading = ref('')

const platforms = [
  { id: 'linux-amd64', label: 'Linux (amd64)' },
  { id: 'darwin-arm64', label: 'macOS (Apple Silicon)' },
  { id: 'windows-amd64', label: 'Windows (amd64)' },
]

async function downloadBinary(platform) {
  downloading.value = platform

  try {
    const response = await fetch(`${orchestratorBaseUrl.value}/worker/download/${platform}`, {
      headers: apiHeaders.value,
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(apiErrorMessage(data, response.status))
    }

    window.location.assign(data.url)
  } catch (error) {
    window.alert(`Download failed: ${error.message}`)
  } finally {
    downloading.value = ''
  }
}
</script>

<style scoped>
.worker-setup {
  display: grid;
  gap: 12px;
}

.worker-setup__heading {
  margin: 0;
  font-family: var(--shell-console-font);
  font-size: 1rem;
  font-weight: 700;
  color: var(--shell-text);
}

.worker-setup__steps {
  display: grid;
  gap: 12px;
  margin: 0;
  padding-left: 18px;
  color: var(--shell-text);
}

.worker-setup__steps li {
  line-height: 1.6;
}

.worker-setup__downloads {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.worker-setup__download {
  min-height: 38px;
  padding: 8px 12px;
  font-size: 0.72rem;
}

.worker-setup__code {
  margin: 10px 0 0;
  padding: 12px 14px;
  border: 1px solid var(--shell-border);
  background: #181b20;
  color: #e9eef6;
  font-family: var(--shell-mono-font);
  font-size: 0.76rem;
  line-height: 1.5;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.worker-setup__note {
  margin: 0;
  color: var(--shell-text-muted);
  font-size: 0.84rem;
  line-height: 1.6;
}
</style>
